import { useState, useEffect, useRef } from 'react'
import * as m from 'framer-motion/m'
import { Container } from './Container'
import { ResponsiveImage } from './ResponsiveImage'

const STOCK_VIDEO = '/assets/stock.mp4'

const PROJECTS = [
  { title: 'Residential Development', location: 'London', type: 'Mixed-use', image: 'AdobeStock_844165661-scaled', fallbackExt: 'jpeg' as const, video: true },
  { title: 'Commercial Asset', location: 'UK', type: 'Office', image: 'AdobeStock_33203909-scaled', fallbackExt: 'jpeg' as const, video: false },
  { title: 'Pipeline Project', location: 'TBC', type: 'Residential', image: 'attorney-8', fallbackExt: 'jpg' as const, video: false },
]

function PortfolioCardVideo({ title }: { title: string }) {
  const [inView, setInView] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) setInView(true)
      },
      { rootMargin: '100px', threshold: 0 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return (
    <div ref={ref} className="absolute inset-0 w-full h-full overflow-hidden">
      {inView ? (
        <video
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          title={title}
          loop
          muted
          autoPlay
          playsInline
          preload="metadata"
          disablePictureInPicture
          disableRemotePlayback
          aria-label={title}
          src={STOCK_VIDEO}
        />
      ) : (
        <div className="w-full h-full bg-[#1e232b]" aria-hidden />
      )}
    </div>
  )
}

export function Portfolio() {
  return (
    <section className="bg-[#1e232b] border-t border-white/10" id="portfolio">
      <Container>
        <div className="py-12 sm:py-16 md:py-20">
          <m.h2
            className="uppercase text-[#cab6a1] text-[28px] sm:text-[36px] md:text-[48px] leading-[1.2em] font-serif mb-10 md:mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Portfolio
          </m.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {PROJECTS.map((project, i) => (
              <m.article
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group rounded-xl overflow-hidden border border-white/15 bg-white/10"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  {project.video ? (
                    <PortfolioCardVideo title={project.title} />
                  ) : (
                    <ResponsiveImage
                      name={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      responsive
                      fallbackExt={project.fallbackExt}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      width={800}
                      height={600}
                    />
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-white text-lg uppercase tracking-wide">{project.title}</h3>
                  <p className="text-white/70 text-sm mt-1 font-sans">{project.location} · {project.type}</p>
                </div>
              </m.article>
            ))}
          </div>
        </div>
      </Container>
    </section>
  )
}
